// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Fragment } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers';
import { AppIcon } from '@shared/ui-kit/components';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useQuestionLayout } from './use-question-layout';

export type { QuestionnaireAnswersAccordionProps } from './use-question-layout';

export const QuestionLayout = defineComponent({
  displayName: 'QuestionLayout',
  useSetup: useQuestionLayout,
  View: ({ blocks }) => {
    return (
      <Card
        variant="outlined"
        className="!mb-6 !rounded-lg"
        data-testid="test-answers-accordion"
      >
        <details className="group">
          <summary
            className="flex cursor-pointer list-none items-center justify-between px-7 py-4 [&::-webkit-details-marker]:hidden"
            data-testid="test-answers-summary"
          >
            <Typography
              variant="h4"
              className="!text-xl !font-semibold !leading-snug !text-secondary-dark"
            >
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.test-answers.title"
                ns="components"
              />
            </Typography>
            <AppIcon
              icon="ArrowDown"
              size="base"
              className="transition-transform duration-200 group-open:rotate-180"
            />
          </summary>

          <CardContent className="space-y-4 !pt-0">
            {blocks.map((block, blockIndex) => (
              <Fragment key={block.id}>
                {blockIndex > 0 && (
                  <hr className="h-[1px] w-full shrink-0 border-gray-300" />
                )}
                <div data-testid={`test-answers-block-${block.id}`}>
                  <Typography
                    component="h3"
                    variant="subtitle1"
                    className="!mb-4 !text-base !font-medium !leading-snug !text-secondary-dark"
                  >
                    {block.title}
                  </Typography>

                  <ol
                    className="m-0 list-outside list-decimal space-y-5 py-0 pl-8 text-base marker:font-semibold marker:text-secondary-dark"
                    data-testid={`test-answers-list-${block.id}`}
                  >
                    {block.questions.map(question => (
                      <li key={question.id} className="pl-2">
                        <div className="flex flex-col">
                          <Typography
                            component="div"
                            variant="body1"
                            className="block !text-base !font-medium !leading-snug !text-secondary-dark"
                          >
                            {question.label as string}
                          </Typography>

                          <Typography
                            component="div"
                            variant="body1"
                            className="!mt-1.5 block !text-base !font-normal !leading-relaxed !text-primary-medium"
                          >
                            <Translation
                              i18nKey={
                                'onboarding-process-overview.result-and-signature.test-answers.answer-line' as any
                              }
                              ns="components"
                              values={{
                                answer: question.answerValue as string,
                              }}
                            />
                          </Typography>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Fragment>
            ))}
          </CardContent>
        </details>
      </Card>
    );
  },
});
